import { type ReactNode, useEffect, useState } from "react";

import { type SidebarNode } from "../../types/content.ts";
import { useLocale } from "../lib/i18n.jsx";
import { useScrollToAnchor } from "../lib/useScrollSpy.js";
import Icon from "../theme/Icon.jsx";
import Search from "./Search.jsx";
import Sidebar from "./Sidebar.jsx";
import Subnav from "./Subnav.jsx";
import VersionFooter from "./VersionFooter.jsx";

// Casca comum a todas as telas. Cada rota entrega os grupos da sidebar já
// prontos (ou nada, para páginas de largura total) e o conteúdo.
interface DocsLayoutProps {
  activeSection?: string;
  sidebar?: SidebarNode[];
  children?: ReactNode;
  contentKey?: string;
}

export default function DocsLayout({ activeSection, sidebar = [], children, contentKey }: DocsLayoutProps) {
  const { t } = useLocale();
  const [mobileNav, setMobileNav] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const scrollToAnchor = useScrollToAnchor();
  const hasSidebar = sidebar.length > 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <Subnav activeSection={activeSection} onSearch={() => setSearchOpen(true)} />
      <Search open={searchOpen} onClose={() => setSearchOpen(false)} />

      <main className={`docs-shell ${hasSidebar ? "" : "no-sidebar"}`}>
        {hasSidebar && (
          <Sidebar
            groups={sidebar}
            open={mobileNav}
            onNavigate={() => setMobileNav(false)}
            onAnchor={(id: string) => {
              scrollToAnchor(id);
              setMobileNav(false);
            }}
          />
        )}

        <div className="docs-main">
          {hasSidebar && (
            <button type="button" className="docs-mobile-toggle" onClick={() => setMobileNav((v) => !v)}>
              <Icon name="menu" size={16} /> {t("navigation")}
            </button>
          )}
          {/* `key` remonta o article a cada página: sem isso o React reaproveita
              a árvore e o scroll interno herdado da página anterior persiste. */}
          <article className="docs-article" key={contentKey}>
            {children}
          </article>
          <VersionFooter />
        </div>
      </main>
    </>
  );
}
